// Renders the SVG overlay (markers, shapes, draft geometry, measure/calibrate
// guides) from the persisted state + ephemeral session. Coordinates are all in
// original-image pixels; the parent #world transform handles zoom/pan.
import { getState, markerDisplayNums } from "./state";
import { markerCatById, shapeCatById, KIND_DEFAULT_COLOR } from "./catalog";
import { session } from "./session";
import type { Marker, Shape } from "./types";

const SVGNS = "http://www.w3.org/2000/svg";
const MARKER_R = 15;
const MARKER_FONT = 11;

export interface OverlayHandlers {
  onMarkerDown: (ev: PointerEvent, id: string) => void;
  onShapeDown: (ev: PointerEvent, id: string) => void;
  onHandleDown: (ev: PointerEvent, id: string, index: number) => void;
}

let overlayEl: SVGSVGElement;
let handlers: OverlayHandlers;
let emptyHintEl: HTMLElement;

export function initOverlay(el: SVGSVGElement, hint: HTMLElement, h: OverlayHandlers): void {
  overlayEl = el;
  emptyHintEl = hint;
  handlers = h;
}

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function centroid(shape: Shape): { x: number; y: number } {
  let cx = 0;
  let cy = 0;
  for (const p of shape.points) {
    cx += p.x;
    cy += p.y;
  }
  const n = shape.points.length || 1;
  return { x: cx / n, y: cy / n };
}

function appendLabel(cx: number, cy: number, text: string): void {
  if (!text) return;
  const g = el("g", { class: "zone-label", transform: `translate(${cx},${cy})` });
  const t = el("text", { x: 0, y: 0, "font-size": 22 });
  t.textContent = text;
  g.appendChild(t);
  overlayEl.appendChild(g);
  try {
    const bb = t.getBBox();
    const rect = el("rect", {
      x: bb.x - 8,
      y: bb.y - 5,
      width: bb.width + 16,
      height: bb.height + 10,
      rx: 6,
    });
    g.insertBefore(rect, t);
  } catch {
    /* getBBox unavailable (jsdom) */
  }
}

function appendLockBadge(cx: number, cy: number): void {
  const g = el("g", { class: "lock-badge", transform: `translate(${cx},${cy})` });
  g.appendChild(el("circle", { r: 11 }));
  g.appendChild(
    el("path", {
      d: "M-4,-1.5 v-2 a4,4 0 0 1 8,0 v2 M-5,-1.5 h10 v7.5 h-10 z",
      fill: "none",
      stroke: "#fff",
      "stroke-width": 1.5,
      "stroke-linejoin": "round",
    })
  );
  overlayEl.appendChild(g);
}

function labelForShape(shape: Shape): string {
  // Tensator-Absperrungen show a label only if explicitly named.
  if (shape.kind === "line") return shape.name || "";
  const cat = shapeCatById(shape.kind, shape.category);
  return shape.name || cat.label;
}

function renderShape(shape: Shape): void {
  const s = session();
  const cat = shapeCatById(shape.kind, shape.category);
  const isSel = s.selection?.type === "shape" && s.selection.id === shape.id;
  const g = el("g", { class: `zone${isSel ? " selected" : ""}` });
  const pts = shape.points.map((p) => `${p.x},${p.y}`).join(" ");

  let primary: SVGElement;
  if (shape.kind === "line") {
    primary = el(shape.closed ? "polygon" : "polyline", {
      points: pts,
      class: "line-fill",
      stroke: cat.color,
    });
  } else {
    primary = el("polygon", {
      points: pts,
      class: shape.kind === "object" ? "object-fill" : "zone-fill",
      fill: cat.color,
      "fill-opacity": 0.32,
      stroke: cat.color,
    });
  }
  primary.addEventListener("pointerdown", (ev) =>
    handlers.onShapeDown(ev as PointerEvent, shape.id)
  );
  g.appendChild(primary);

  if (shape.kind === "line") {
    for (const p of shape.points) {
      const post = el("circle", {
        cx: p.x,
        cy: p.y,
        r: 9,
        class: "line-post",
        fill: "#fff",
        stroke: cat.color,
      });
      post.addEventListener("pointerdown", (ev) =>
        handlers.onShapeDown(ev as PointerEvent, shape.id)
      );
      g.appendChild(post);
    }
  }
  overlayEl.appendChild(g);

  const c = centroid(shape);
  appendLabel(c.x, c.y, labelForShape(shape));
  if (shape.locked) appendLockBadge(c.x, c.y + 24);

  if (isSel && !shape.locked) {
    shape.points.forEach((p, idx) => {
      const handle = el("circle", {
        cx: p.x,
        cy: p.y,
        r: 12,
        class: "zone-handle",
        fill: cat.color,
      });
      handle.addEventListener("pointerdown", (ev) =>
        handlers.onHandleDown(ev as PointerEvent, shape.id, idx)
      );
      overlayEl.appendChild(handle);
    });
  }
}

function renderMarker(marker: Marker, displayNum: number): void {
  const s = session();
  const cat = markerCatById(marker.category);
  const isSel = s.selection?.type === "marker" && s.selection.id === marker.id;
  const g = el("g", {
    class: `marker${isSel ? " selected" : ""}`,
    transform: `translate(${marker.x},${marker.y})`,
  });
  if (cat.shape === "rect") {
    const d = MARKER_R * 2;
    g.appendChild(
      el("rect", { x: -MARKER_R - 6, y: -MARKER_R - 6, width: d + 12, height: d + 12, rx: 6, class: "halo" })
    );
    g.appendChild(
      el("rect", { x: -MARKER_R, y: -MARKER_R, width: d, height: d, rx: 4, class: "disc", fill: cat.color })
    );
  } else {
    g.appendChild(el("circle", { r: MARKER_R + 6, class: "halo" }));
    g.appendChild(el("circle", { r: MARKER_R, class: "disc", fill: cat.color }));
  }
  const t = el("text", { y: 1, "font-size": MARKER_FONT });
  t.textContent = String(displayNum);
  g.appendChild(t);
  g.addEventListener("pointerdown", (ev) =>
    handlers.onMarkerDown(ev as PointerEvent, marker.id)
  );
  overlayEl.appendChild(g);
  if (marker.locked) appendLockBadge(marker.x + MARKER_R + 9, marker.y - MARKER_R - 9);
}

function renderDraft(): void {
  const s = session();

  if (s.mode === "tensator") {
    // Live preview: belt between consecutive posts + a post at each click.
    // Shown even before the first click so hovering near an existing
    // tensator's post already previews the snap (chains "stick together").
    const chain = s.hoverPoint ? [...s.shapeDraft, s.hoverPoint] : s.shapeDraft;
    if (chain.length > 1) {
      overlayEl.appendChild(
        el("polyline", {
          points: chain.map((p) => `${p.x},${p.y}`).join(" "),
          class: "line-fill",
          stroke: KIND_DEFAULT_COLOR.line,
          "stroke-opacity": 0.75,
        })
      );
    }
    for (const p of s.shapeDraft) {
      overlayEl.appendChild(
        el("circle", { cx: p.x, cy: p.y, r: 9, class: "line-post", fill: "#fff", stroke: KIND_DEFAULT_COLOR.line })
      );
    }
    if (s.hoverPoint) {
      overlayEl.appendChild(
        el("circle", {
          cx: s.hoverPoint.x,
          cy: s.hoverPoint.y,
          r: s.snapClose ? 15 : 9,
          class: s.snapClose ? "draft-snap" : "line-post",
          fill: s.snapClose ? "none" : "#fff",
          stroke: KIND_DEFAULT_COLOR.line,
        })
      );
    }
  } else if (s.mode === "shape" && s.shapeDraft.length) {
    const draft = s.shapeDraft;
    const cat = shapeCatById(s.shapeKind, s.shapeCat);
    // Rubber-band point: snap onto the first vertex when close enough to close.
    const rubber = s.snapClose ? draft[0]! : s.hoverPoint;
    const chain = rubber ? [...draft, rubber] : draft;

    // Filled interior preview once the polygon has area.
    if (chain.length >= 3) {
      overlayEl.appendChild(
        el("polygon", {
          points: chain.map((p) => `${p.x},${p.y}`).join(" "),
          class: "draft-fill",
          fill: cat.color,
        })
      );
    }
    if (chain.length > 1) {
      overlayEl.appendChild(
        el("polyline", { points: chain.map((p) => `${p.x},${p.y}`).join(" "), class: "draft-line" })
      );
    }
    for (const p of draft) {
      overlayEl.appendChild(el("circle", { cx: p.x, cy: p.y, r: 9, class: "draft-pt" }));
    }
    if (s.snapClose && draft[0]) {
      overlayEl.appendChild(el("circle", { cx: draft[0].x, cy: draft[0].y, r: 15, class: "draft-snap" }));
    }
  }

  const drawGuide = (points: typeof s.measurePts, lineClass: string, ptClass: string) => {
    if (!points.length) return;
    if (points.length === 2) {
      overlayEl.appendChild(
        el("line", {
          x1: points[0]!.x,
          y1: points[0]!.y,
          x2: points[1]!.x,
          y2: points[1]!.y,
          class: lineClass,
        })
      );
    }
    for (const p of points) {
      overlayEl.appendChild(el("circle", { cx: p.x, cy: p.y, r: 9, class: ptClass }));
    }
  };
  drawGuide(s.measurePts, "measure-line", "measure-pt");
  drawGuide(s.calibratePts, "measure-line", "measure-pt");

  if (s.mode === "measure" && s.measurePts.length === 2) {
    const [a, b] = s.measurePts as [{ x: number; y: number }, { x: number; y: number }];
    const dist = Math.hypot(b.x - a.x, b.y - a.y) / getState().scale.pxPerMeter;
    appendLabel(
      (a.x + b.x) / 2,
      (a.y + b.y) / 2,
      `${dist.toLocaleString("de-DE", { maximumFractionDigits: 2 })} m`
    );
  }
}

export function renderOverlay(): void {
  const { markers, shapes } = getState();
  const { layers } = session();
  overlayEl.replaceChildren();

  // Order matters: shapes below markers. Layer toggles hide whole groups.
  for (const shape of shapes) {
    if (shape.kind === "line" ? layers.tensatoren : layers.flaechen) renderShape(shape);
  }
  if (layers.objekte) {
    const nums = markerDisplayNums();
    for (const marker of markers) renderMarker(marker, nums.get(marker.id) ?? marker.num);
  }
  renderDraft();

  const s = session();
  emptyHintEl.style.display =
    markers.length === 0 && shapes.length === 0 && s.mode === "select" ? "block" : "none";
}
